/**
 * @author Hiroki Moto
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import keys from 'ramda/src/keys';

const moment = extendMoment(originalMoment);

const ReviewChart = ({ company, date }) => {
    const reviewChart = useRef(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        const { ratingData, interactionData } = generateReviewsData();
        const minDate = new Date(date.start.year(), date.start.month(), date.start.date());
        const maxDate = new Date(date.end.year(), date.end.month(), date.end.date());

        // Create Reviews chart instance
        let x = am4core.create('reviews-chart', am4charts.XYChart);
        x.paddingRight = 20;
        // Add data
        x.data = ratingData;
        // Create axes
        let reviewsDateAxis = x.xAxes.push(new am4charts.DateAxis());
        reviewsDateAxis.startLocation = 0.5;
        reviewsDateAxis.endLocation = 0.5;
        reviewsDateAxis.renderer.grid.template.location = 0;
        reviewsDateAxis.renderer.minGridDistance = 50;
        reviewsDateAxis.baseInterval = { timeUnit: 'month', count: 1 };
        reviewsDateAxis.min = minDate.getTime();
        reviewsDateAxis.max = maxDate.getTime();
        reviewsDateAxis.skipEmptyPeriods = true;
        // Create value axis
        let reviewsValueAxis = x.yAxes.push(new am4charts.ValueAxis());
        reviewsValueAxis.renderer.opposite = true;
        reviewsValueAxis.min = 0;
        reviewsValueAxis.max = 150;
        // Create series
        let reviewsSeries1 = createSeries(x, 'count', 'New Reviews', '#f00');
        let reviewsSeries2 = createSeries(x, 'avg', 'Avg Ratings', '#0f0');
        let reviewsSeries3 = createSeries(x, 'recommended', 'Recommended', '#00f');
        reviewsSeries2.hidden = true;
        reviewsSeries3.hidden = true;
        reviewChart.current = x;

        return () => {
            x.dispose();
        };
    }, []);

    useEffect(() => {
        setSelectedProduct(company);
    }, [company]);

    useEffect(() => {
        if (selectedProduct) {
            const { ratingData } = generateReviewsData();
            reviewChart.current.data = ratingData;
        }
    }, [selectedProduct]);

    const generateReviewsData = () => {
        const productData = selectedProduct || company;
        if (productData?.rating_analytics?.data) {
            const { rating_analytics } = productData.rating_analytics.data;
            const ratingData = keys(rating_analytics).map((dateKey) => {
                const { rating_count, rating_avg, rating_recommended_avg } = rating_analytics[dateKey].month;
                const keyStr = dateKey.split('_');
                const yearStr = keyStr[1].substring(0, 4);
                const monthStr = keyStr[1].substring(4);
                return {
                    date: new Date(yearStr, monthStr, 1),
                    count: rating_count,
                    avg: rating_avg,
                    recommended: rating_recommended_avg,
                };
            });
            return { ratingData };
        }
        return { ratingData: [] };
    };

    const createSeries = (chart, field, name, color) => {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = field;
        series.dataFields.dateX = 'date';
        series.name = name;
        series.tooltipText = '{dateX}: [b]{valueY}[/]';
        series.stroke = am4core.color(color);
        series.strokeWidth = 1;
        series.tensionX = 0.8;
        series.connect = true;
        return series;
    };

    const handleSelectReviews = (e) => {
        if (reviewChart?.current?.series) {
            const currentSeries = reviewChart.current.series.getIndex(e[0]);
            reviewChart.current.series.each((series) => {
                if (series != currentSeries) {
                    series.hide();
                }
            });
            currentSeries.show();
        }
    };

    return <div className="visibility-review" id="reviews-chart" />;
};

ReviewChart.propTypes = {
    company: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        products: PropTypes.array,
        stats: PropTypes.shape({
            product_count: PropTypes.number,
            popularity_score: PropTypes.number,
            rating_recommended_avg: PropTypes.number,
            rating_count: PropTypes.number,
            rating_dealership_count: PropTypes.number,
        }),
    }),
    date: PropTypes.any,
};

ReviewChart.defaultProps = {
    company: {
        id: 0,
        name: '',
        products: [],
        stats: {
            product_count: 0,
            popularity_score: 0,
            rating_recommended_avg: 0,
            rating_count: 0,
            rating_dealership_count: 0,
        },
    },
    date: moment.range(moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')),
};

export default ReviewChart;
